/**
 * Hotfix v1.26.4 — single source of truth for client-side auth cleanup.
 *
 * Why this exists
 * ---------------
 * Before this file, four code paths each cleared "some" auth state:
 *
 *   • apiFetch 401 handler (services/api.js) — cleared sb-* keys + session
 *     flags but NOT localStorage.qm_voter. On the forced redirect the next
 *     page load read qm_voter back, resolved to the directory view, fired
 *     the same /candidates call, got another 401 — infinite reload loop.
 *
 *   • handleResetVoter (useAppState.js) — cleared qm_voter + friends but
 *     never called supabase.auth.signOut(), so the JWT survived "logout".
 *
 *   • handleAdminLogout — cleared qm_admin session flag only; Supabase JWT
 *     lived on.
 *
 *   • handlePoLogout — called supabase.auth.signOut() but skipped the
 *     legacy sb-*-auth-token purge, so pre-v1.23.0 sessions resurrected
 *     on the next page load.
 *
 * The bug the user reported ("clearing session and storages — not just
 * going to the base url") was all four paths drifting independently.
 * Centralising lets every caller get the full treatment for free and
 * makes the behaviour testable with one unit.
 *
 * Contract
 * --------
 * `clearAllClientAuth()` is idempotent, never throws, and on return:
 *   • No Supabase session is active (signOut best-effort)
 *   • No Supabase keys remain in localStorage (current + legacy sb-*)
 *   • No voter / civics / xp / chats localStorage keys remain
 *   • No admin / PO sessionStorage flags remain
 *
 * After calling it, the caller is responsible for the UI side — pushing
 * a history state, resetting React state, redirecting, etc. We don't do
 * that here because the four callsites want different destinations.
 */

import { supabase } from "./supabase.js";

/**
 * Clear every piece of client-side auth state: Supabase session,
 * localStorage keys (current + legacy), sessionStorage flags.
 *
 * Safe to call from any auth-cleanup path. Never throws — catches its
 * own errors so a failing signOut() doesn't block the storage purge.
 */
export async function clearAllClientAuth() {
  // 1. End the Supabase session. Best-effort — if the network is down or
  // the SDK is mid-init we still need to wipe storage, so swallow errors.
  try {
    await supabase.auth.signOut();
  } catch {
    /* purge below is the real backstop */
  }

  // 2. Purge ALL Supabase session keys from localStorage.
  //
  // signOut() above only clears the configured storageKey ("qm-auth-token").
  // Legacy sb-<project>-auth-token keys written before v1.23.0's storageKey
  // change survive and are auto-read on the next page load, resurrecting a
  // supposedly-cleared session. We clear every known shape so logout always
  // sticks, regardless of which key the token was written under.
  try {
    localStorage.removeItem("qm-auth-token");
    Object.keys(localStorage)
      .filter((k) => k.startsWith("sb-") && k.endsWith("-auth-token"))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* private-mode / storage-disabled browsers */
  }

  // 3. Wipe voter-facing localStorage.
  //
  // qm_voter is the view-resolver key (useAppState reads it to decide
  // whether to show the directory or the onboarding screen). If we don't
  // clear it alongside the JWT, a reload lands on the directory with no
  // auth — /candidates 401s — apiFetch redirects — reload loops.
  try {
    localStorage.removeItem("qm_voter");
    // Derived caches — tidy, not critical, but keeps the next onboarding
    // clean (stale XP / chat history shouldn't bleed across sessions).
    localStorage.removeItem("qm_civics"); // pre-RWD-8 legacy key
    localStorage.removeItem("qm_xp");
    localStorage.removeItem("qm_chats");
  } catch {
    /* ditto */
  }

  // 4. Wipe sessionStorage auth flags (admin + PO tabs).
  //
  // These gate view-resolver branches for the admin / persona-owner roles.
  // Same reload-loop risk as qm_voter if they stay behind.
  try {
    sessionStorage.removeItem("qm_admin");
    sessionStorage.removeItem("qm_admin_token");
    sessionStorage.removeItem("qm_po");
    sessionStorage.removeItem("qm_po_persona_id");
  } catch {
    /* private-mode */
  }
}

/**
 * Synchronous subset of clearAllClientAuth — same storage wipes minus the
 * async supabase.auth.signOut() call. Used by the apiFetch 401 handler
 * where we're about to `window.location.href =` a full reload anyway,
 * and awaiting signOut() adds a visible delay before the redirect.
 *
 * The SDK reads from localStorage on its next init, so the localStorage
 * purge is sufficient — the missing signOut just skips a server-side
 * revocation RPC that would happen after redirect anyway.
 */
export function clearAllClientAuthSync() {
  try {
    localStorage.removeItem("qm-auth-token");
    Object.keys(localStorage)
      .filter((k) => k.startsWith("sb-") && k.endsWith("-auth-token"))
      .forEach((k) => localStorage.removeItem(k));
    localStorage.removeItem("qm_voter");
    localStorage.removeItem("qm_civics");
    localStorage.removeItem("qm_xp");
    localStorage.removeItem("qm_chats");
  } catch {
    /* private-mode */
  }
  try {
    sessionStorage.removeItem("qm_admin");
    sessionStorage.removeItem("qm_admin_token");
    sessionStorage.removeItem("qm_po");
    sessionStorage.removeItem("qm_po_persona_id");
  } catch {
    /* private-mode */
  }
}
